/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import type { ColumnsType } from 'antd/es/table';

import { role, Subtenant, UserSubtenant } from '@/client';
import { usePaginatedUserSubtenantsQuery } from '@/hooks/query/usePaginatedUserSubtenantsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Select } from '@/primitives/Select';
import { Skeleton } from '@/primitives/Skeleton';
import { Text } from '@/primitives/Text';

export function useUserSubtenantListColumns(
  handleUpdateUserSubtenantRole: (args: { subtenantId: string; userSubtenantRole: role }) => void,
  userSubtenantsQuery: ReturnType<typeof usePaginatedUserSubtenantsQuery>,
) {
  const { t } = useMimicTranslation('users');

  const options = [
    { label: t('updateSubtenants.noAccess'), value: 'none' },
    { label: t('updateSubtenants.inherit'), value: 'inherit' },
    { label: t('updateSubtenants.admin'), value: 'admin' },
    { label: t('updateSubtenants.editor'), value: 'editor' },
    { label: t('updateSubtenants.viewer'), value: 'viewer' },
  ];

  const columns: ColumnsType<Subtenant> = [
    {
      title: t('updateSubtenants.name'),
      dataIndex: 'name',
      key: 'id',
      render: (_, subtenant) => {
        return <Text size="md">{subtenant.name}</Text>;
      },
    },
    {
      title: t('updateSubtenants.role'),
      dataIndex: 'parent',
      width: 250,
      key: 'id',
      render: (_, subtenant) => {
        if (userSubtenantsQuery.isFetching) return <Skeleton loading />;

        const userSubtenantsData = (userSubtenantsQuery.data?.data || []) as Array<UserSubtenant>;
        const userSubtenant = userSubtenantsData.find((us) => us.subtenant.id === subtenant.id);
        return (
          <Select
            data-testid="user-role-select"
            style={{ width: '100%', minWidth: 200 }}
            onChange={(option) => {
              handleUpdateUserSubtenantRole({ subtenantId: subtenant.id, userSubtenantRole: option });
            }}
            filterOption={false}
            options={options}
            defaultValue={userSubtenant?.role || 'No access'}
          />
        );
      },
    },
  ];

  return columns;
}
